import { useState } from 'react';
import { useQuery } from 'react-query';
import { Search, FileText, ChevronLeft, ChevronRight, Filter, X, MapPin, Calendar, User, Home, Building2 } from 'lucide-react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';

// Custom styles to fix Leaflet z-index issues
const leafletStyles = `
  .leaflet-pane {
    z-index: 10 !important;
  }
  .leaflet-top,
  .leaflet-bottom {
    z-index: 11 !important;
  }
`;

const PublicSearch = () => {
  const { user } = useAuth();
  const isLoggedIn = !!user;

  // Search state - only triggers on button click
  const [searchTerm, setSearchTerm] = useState('');
  const [submittedSearchTerm, setSubmittedSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Post-search filter state (only shown after results)
  const [selectedSubdivision, setSelectedSubdivision] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [sortBy, setSortBy] = useState('issue_date');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal state for permit details
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Query for subdivisions (for filter dropdown)
  const { data: subdivisionsData } = useQuery(
    ['subdivisions'],
    async () => {
      const response = await api.get('/subdivisions');
      return response.data;
    }
  );

  // Search query - only runs when hasSearched is true
  const { data: searchData, isLoading, error } = useQuery(
    ['public-permits', submittedSearchTerm, selectedSubdivision, selectedStatus, currentPage, sortBy, sortOrder],
    async () => {
      let url = `/permits/public?page=${currentPage}&limit=${itemsPerPage}&sortBy=${sortBy}&sortOrder=${sortOrder}`;
      if (submittedSearchTerm) {
        url += `&search=${encodeURIComponent(submittedSearchTerm)}`;
      }
      if (selectedSubdivision) {
        url += `&subdivisionId=${selectedSubdivision}`;
      }
      if (selectedStatus) {
        url += `&status=${selectedStatus}`;
      }
      const response = await api.get(url);
      return response.data;
    },
    {
      enabled: hasSearched && !!submittedSearchTerm,
      keepPreviousData: false,
      staleTime: 0,
    }
  );

  // Handle search button click
  const handleSearch = (e) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      alert('Please enter search keywords (Permit Holder Name, Permit Number, Area, or Issue Date)');
      return;
    }

    setSubmittedSearchTerm(searchTerm.trim());
    setHasSearched(true);
    setCurrentPage(1);
    // Reset filters on new search
    setSelectedSubdivision('');
    setSelectedStatus('');
    setSortBy('issue_date');
    setSortOrder('DESC');
  };

  // Handle subdivision filter change (post-search)
  const handleSubdivisionChange = (e) => {
    setSelectedSubdivision(e.target.value);
    setCurrentPage(1);
  };

  // Handle status filter change (post-search)
  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1);
  };

  // Handle sort change (post-search)
  const handleSortChange = (e) => {
    const value = e.target.value;
    const parts = value.split('_');
    const newSortOrder = parts[parts.length - 1];
    const newSortBy = parts.slice(0, -1).join('_');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  // Clear post-search filters only (keep search term)
  const handleClearFilters = () => {
    setSelectedSubdivision('');
    setSelectedStatus('');
    setSortBy('issue_date');
    setSortOrder('DESC');
    setCurrentPage(1);
  };

  // Clear everything and start fresh
  const handleNewSearch = () => {
    setSearchTerm('');
    setSubmittedSearchTerm('');
    setSelectedSubdivision('');
    setSelectedStatus('');
    setSortBy('issue_date');
    setSortOrder('DESC');
    setCurrentPage(1);
    setHasSearched(false);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle permit tile click - open modal
  const handlePermitClick = (permit) => {
    setSelectedPermit(permit);
    setShowModal(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPermit(null);
  };

  const permits = searchData?.permits || [];
  const subdivisions = subdivisionsData?.subdivisions || [];
  const pagination = searchData?.pagination || {};

  // Check if any post-search filters are active
  const hasActiveFilters = selectedSubdivision || selectedStatus || sortBy !== 'issue_date' || sortOrder !== 'DESC';

  return (
    <div className="min-h-screen flex flex-col">
      <style>{leafletStyles}</style>
      {!isLoggedIn && <Navbar />}

      <main className="flex-grow bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search Section Header */}
          <div className="text-center mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Public Permit Search</h2>
            <p className="text-gray-600 text-sm max-w-2xl mx-auto">
              Search for land permits in Mon District by Permit Holder Name, Permit Number, Area, or Issue Date
            </p>
          </div>

          {/* Search Form - Only search input and button */}
          <div className="bg-white shadow rounded-lg p-6 mb-8">
            <form onSubmit={handleSearch}>
              <div className="mb-4">
                <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                  Search Keywords <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-secondary-400" />
                  <input
                    type="text"
                    id="search"
                    className="input pl-10 text-base w-full"
                    placeholder="Enter Permit Holder Name, Permit Number, Area (sq ft), or Issue Date..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <p className="mt-2 text-xs text-secondary-500">
                  Search by permit holder name, permit number, land area, or issue date
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="submit"
                  className="btn btn-primary px-6"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Search Permits
                </button>
                {hasSearched && (
                  <button
                    type="button"
                    onClick={handleNewSearch}
                    className="btn bg-secondary-100 text-secondary-700 hover:bg-secondary-200 px-4"
                  >
                    <X className="h-4 w-4 mr-2" />
                    New Search
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Search Results */}
          {hasSearched && (
            <div className="bg-white shadow rounded-lg overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-secondary-600">Searching...</span>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <p className="text-red-600">Error occurred while searching. Please try again.</p>
                </div>
              ) : permits.length > 0 ? (
                <>
                  {/* Post-Search Filters Section */}
                  <div className="bg-secondary-50 px-6 py-4 border-b border-secondary-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Filter className="h-4 w-4 text-secondary-600" />
                      <span className="text-sm font-medium text-secondary-700">Filter & Sort Results</span>
                      <span className="text-sm text-secondary-500">
                        ({pagination.totalPermits} permit{pagination.totalPermits !== 1 ? 's' : ''} found for "{submittedSearchTerm}")
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      {/* Subdivision Filter */}
                      <div>
                        <label htmlFor="subdivision-filter" className="block text-xs font-medium text-secondary-600 mb-1">
                          Filter by Subdivision
                        </label>
                        <select
                          id="subdivision-filter"
                          value={selectedSubdivision}
                          onChange={handleSubdivisionChange}
                          className="input w-full text-sm"
                        >
                          <option value="">All Subdivisions</option>
                          {subdivisions.map((subdivision) => (
                            <option key={subdivision.id} value={subdivision.id}>
                              {subdivision.name} {subdivision.code ? `(${subdivision.code})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div>
                        <label htmlFor="status-filter" className="block text-xs font-medium text-secondary-600 mb-1">
                          Filter by Status
                        </label>
                        <select
                          id="status-filter"
                          value={selectedStatus}
                          onChange={handleStatusChange}
                          className="input w-full text-sm"
                        >
                          <option value="">All Statuses</option>
                          <option value="Active">Active</option>
                          <option value="Renewal_Needed">Renewal Needed</option>
                          <option value="Cancelled">Cancelled</option>
                          <option value="Transferred">Transferred</option>
                          <option value="Pending_EAC_Review">Pending EAC Review</option>
                          <option value="Pending_SDO_Review">Pending SDO Review</option>
                          <option value="Pending_ADC_Review">Pending ADC Review</option>
                          <option value="Pending_DC_Approval">Pending DC Approval</option>
                        </select>
                      </div>

                      {/* Sort Filter */}
                      <div>
                        <label htmlFor="sort-filter" className="block text-xs font-medium text-secondary-600 mb-1">
                          Sort Results By
                        </label>
                        <select
                          id="sort-filter"
                          value={`${sortBy}_${sortOrder}`}
                          onChange={handleSortChange}
                          className="input w-full text-sm"
                        >
                          <option value="issue_date_DESC">Issue Date (Newest First)</option>
                          <option value="issue_date_ASC">Issue Date (Oldest First)</option>
                          <option value="permit_no_ASC">Permit Number (A-Z)</option>
                          <option value="permit_no_DESC">Permit Number (Z-A)</option>
                          <option value="name_ASC">Owner Name (A-Z)</option>
                          <option value="name_DESC">Owner Name (Z-A)</option>
                          <option value="size_sqft_DESC">Area (Largest First)</option>
                          <option value="size_sqft_ASC">Area (Smallest First)</option>
                        </select>
                      </div>

                      {/* Clear Filters Button */}
                      <div className="flex items-end">
                        {hasActiveFilters && (
                          <button
                            type="button"
                            onClick={handleClearFilters}
                            className="btn bg-white text-secondary-700 hover:bg-secondary-100 border border-secondary-300 text-sm w-full"
                          >
                            <X className="h-4 w-4 mr-1" />
                            Clear Filters
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Active Filters Summary */}
                    {hasActiveFilters && (
                      <div className="mt-3 flex items-center gap-2 text-xs text-secondary-600 flex-wrap">
                        <span className="font-medium">Active filters:</span>
                        {selectedSubdivision && (
                          <span className="bg-white px-2 py-1 rounded border border-secondary-300">
                            Subdivision: {subdivisions.find(s => s.id === parseInt(selectedSubdivision))?.name}
                          </span>
                        )}
                        {selectedStatus && (
                          <span className={`px-2 py-1 rounded border ${
                            selectedStatus === 'Active' ? 'bg-green-50 border-green-300 text-green-700' :
                            selectedStatus === 'Renewal_Needed' ? 'bg-yellow-50 border-yellow-300 text-yellow-700' :
                            selectedStatus === 'Cancelled' ? 'bg-red-50 border-red-300 text-red-700' :
                            'bg-gray-50 border-gray-300 text-gray-700'
                          }`}>
                            Status: {selectedStatus === 'Renewal_Needed' ? 'Renewal Needed' : selectedStatus.replace(/_/g, ' ')}
                          </span>
                        )}
                        {(sortBy !== 'issue_date' || sortOrder !== 'DESC') && (
                          <span className="bg-blue-50 px-2 py-1 rounded border border-blue-300 text-blue-700">
                            Sort: {
                              sortBy === 'issue_date' ? (sortOrder === 'ASC' ? 'Oldest First' : 'Newest First') :
                              sortBy === 'permit_no' ? (sortOrder === 'ASC' ? 'Permit A-Z' : 'Permit Z-A') :
                              sortBy === 'name' ? (sortOrder === 'ASC' ? 'Name A-Z' : 'Name Z-A') :
                              sortBy === 'size_sqft' ? (sortOrder === 'DESC' ? 'Largest Area' : 'Smallest Area') : ''
                            }
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Results List */}
                  <div className="divide-y divide-secondary-200">
                    {permits.map((permit) => {
                      const hasLocation = permit.latitude && permit.longitude;

                      return (
                        <div
                          key={permit.id}
                          className="p-6 hover:bg-secondary-50 cursor-pointer transition-colors"
                          onClick={() => handlePermitClick(permit)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-medium text-secondary-900">
                                {permit.permit_no}
                              </h3>
                              <p className="text-secondary-700 mt-1 text-sm">{permit.name}</p>
                              <p className="text-secondary-600 mt-1 text-sm">{permit.village}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-secondary-600">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  permit.status === 'Active' ? 'bg-green-100 text-green-800' :
                                  permit.status === 'Renewal_Needed' ? 'bg-yellow-100 text-yellow-800' :
                                  permit.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                                  permit.status === 'Transferred' ? 'bg-blue-100 text-blue-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {permit.status === 'Renewal_Needed' ? 'Renewal Needed' : permit.status}
                                </span>
                                {permit.size_sqft && (
                                  <span>Area: {permit.size_sqft} sq ft</span>
                                )}
                                {permit.issue_date && (
                                  <span>Date of Issue: {formatDate(permit.issue_date)}</span>
                                )}
                                {permit.subdivision && (
                                  <span>Subdivision: {permit.subdivision.name}</span>
                                )}
                              </div>
                              <p className="mt-2 text-xs text-primary-600">Click to view details</p>
                            </div>

                            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                              {/* Small Map Preview */}
                              {hasLocation && (
                                <div className="border-2 border-gray-300 rounded overflow-hidden" style={{ width: '150px', height: '150px' }}>
                                  <MapContainer
                                    center={[parseFloat(permit.latitude), parseFloat(permit.longitude)]}
                                    zoom={14}
                                    style={{ height: '100%', width: '100%' }}
                                    zoomControl={false}
                                    attributionControl={false}
                                    dragging={false}
                                    scrollWheelZoom={false}
                                    doubleClickZoom={false}
                                    touchZoom={false}
                                    keyboard={false}
                                  >
                                    <TileLayer
                                      url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                      maxZoom={18}
                                    />
                                    <Marker position={[parseFloat(permit.latitude), parseFloat(permit.longitude)]} />
                                  </MapContainer>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Controls */}
                  {pagination.totalPages > 1 && (
                    <div className="bg-secondary-50 px-6 py-4 border-t border-secondary-200">
                      {/* Desktop Pagination */}
                      <div className="hidden sm:flex items-center justify-between">
                        <div className="text-sm text-secondary-700">
                          Page {pagination.currentPage} of {pagination.totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={!pagination.hasPrev}
                            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                              pagination.hasPrev
                                ? 'bg-white text-secondary-700 hover:bg-secondary-100 border border-secondary-300'
                                : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                            }`}
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </button>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!pagination.hasNext}
                            className={`flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                              pagination.hasNext
                                ? 'bg-white text-secondary-700 hover:bg-secondary-100 border border-secondary-300'
                                : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                            }`}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>

                      {/* Mobile Pagination */}
                      <div className="sm:hidden">
                        <div className="text-sm text-secondary-700 text-center mb-3">
                          Page {pagination.currentPage} of {pagination.totalPages}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={!pagination.hasPrev}
                            className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md ${
                              pagination.hasPrev
                                ? 'bg-white text-secondary-700 border border-secondary-300'
                                : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                            }`}
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </button>
                          <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={!pagination.hasNext}
                            className={`flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium rounded-md ${
                              pagination.hasNext
                                ? 'bg-white text-secondary-700 border border-secondary-300'
                                : 'bg-secondary-200 text-secondary-400 cursor-not-allowed'
                            }`}
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-10 w-10 text-secondary-300" />
                  <h3 className="mt-2 text-base font-medium text-secondary-900">No permits found</h3>
                  <p className="mt-1 text-sm text-secondary-600">
                    No permits match your search for "{submittedSearchTerm}". Try different keywords.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Empty State - Before Search */}
          {!hasSearched && (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-secondary-300" />
              <h3 className="mt-4 text-base font-medium text-secondary-900">
                Search Permits
              </h3>
              <p className="mt-2 text-sm text-secondary-600 max-w-md mx-auto">
                Enter search keywords above to find land permits in Mon District.
                You can search by permit holder name, permit number, area, or issue date.
              </p>
            </div>
          )}

        </div>
      </main>

      {!isLoggedIn && <Footer />}

      {/* Permit Details Modal */}
      {showModal && selectedPermit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 z-20 bg-white px-6 py-4 border-b border-secondary-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900">Permit Details</h3>
                <p className="text-sm text-secondary-600">{selectedPermit.permit_no}</p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-secondary-400 hover:text-secondary-600 p-1"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              {/* Map Section */}
              {selectedPermit.latitude && selectedPermit.longitude && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-secondary-700 mb-2 flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    Location
                  </h4>
                  <div className="border-2 border-gray-300 rounded-lg overflow-hidden" style={{ height: '250px' }}>
                    <MapContainer
                      center={[parseFloat(selectedPermit.latitude), parseFloat(selectedPermit.longitude)]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                      zoomControl={true}
                      attributionControl={false}
                    >
                      <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        maxZoom={18}
                      />
                      <Marker position={[parseFloat(selectedPermit.latitude), parseFloat(selectedPermit.longitude)]} />
                    </MapContainer>
                  </div>
                  <p className="mt-2 text-xs text-secondary-500">
                    Coordinates: {selectedPermit.latitude}, {selectedPermit.longitude}
                  </p>
                </div>
              )}

              {/* Permit Holder Information */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  Permit Holder Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50 rounded-lg p-4">
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Permit Holder Name</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Father's Name</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.father_name || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Location Details */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                  <Home className="h-4 w-4 mr-1" />
                  Location Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50 rounded-lg p-4">
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Village</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.village || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Post Office</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.post_office || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Police Station</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.police_station || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">District</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.district || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Land Boundaries */}
              {(selectedPermit.north_boundary || selectedPermit.south_boundary || selectedPermit.east_boundary || selectedPermit.west_boundary) && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    Land Boundaries
                  </h4>
                  <div className="grid grid-cols-2 gap-3 bg-secondary-50 rounded-lg p-4">
                    <div>
                      <label className="text-xs font-medium text-secondary-500">North</label>
                      <p className="text-sm text-secondary-900">{selectedPermit.north_boundary || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500">South</label>
                      <p className="text-sm text-secondary-900">{selectedPermit.south_boundary || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500">East</label>
                      <p className="text-sm text-secondary-900">{selectedPermit.east_boundary || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-secondary-500">West</label>
                      <p className="text-sm text-secondary-900">{selectedPermit.west_boundary || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Permit Details */}
              <div className="mb-6">
                <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                  <FileText className="h-4 w-4 mr-1" />
                  Permit Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50 rounded-lg p-4">
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Permit Number</label>
                    <p className="text-sm text-secondary-900 font-medium">{selectedPermit.permit_no || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Area (Sq. Ft.)</label>
                    <p className="text-sm text-secondary-900">{selectedPermit.size_sqft || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Issue Date</label>
                    <p className="text-sm text-secondary-900">
                      {selectedPermit.issue_date ? formatDate(selectedPermit.issue_date) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Status</label>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      selectedPermit.status === 'Active' ? 'bg-green-100 text-green-800' :
                      selectedPermit.status === 'Renewal_Needed' ? 'bg-yellow-100 text-yellow-800' :
                      selectedPermit.status === 'Cancelled' ? 'bg-red-100 text-red-800' :
                      selectedPermit.status === 'Transferred' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedPermit.status === 'Renewal_Needed' ? 'Renewal Needed' : selectedPermit.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Subdivision Information */}
              {selectedPermit.subdivision && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                    <Building2 className="h-4 w-4 mr-1" />
                    Administrative Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50 rounded-lg p-4">
                    <div>
                      <label className="text-xs font-medium text-secondary-500">Subdivision</label>
                      <p className="text-sm text-secondary-900">{selectedPermit.subdivision.name}</p>
                    </div>
                    {selectedPermit.subdivision.code && (
                      <div>
                        <label className="text-xs font-medium text-secondary-500">Subdivision Code</label>
                        <p className="text-sm text-secondary-900">{selectedPermit.subdivision.code}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Boundary Points */}
              {selectedPermit.boundary_points && selectedPermit.boundary_points.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                    <MapPin className="h-4 w-4 mr-1" />
                    Boundary Points
                  </h4>
                  <div className="bg-secondary-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {selectedPermit.boundary_points.map((point, index) => (
                        <div key={index} className="bg-white rounded p-2 border border-secondary-200">
                          <label className="text-xs font-medium text-secondary-500">{point.direction}</label>
                          <p className="text-sm text-secondary-900">{point.value || 'N/A'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div>
                <h4 className="text-sm font-medium text-secondary-700 mb-3 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Record Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-secondary-50 rounded-lg p-4">
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Created At</label>
                    <p className="text-sm text-secondary-900">
                      {selectedPermit.createdAt ? new Date(selectedPermit.createdAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-secondary-500">Last Updated</label>
                    <p className="text-sm text-secondary-900">
                      {selectedPermit.updatedAt ? new Date(selectedPermit.updatedAt).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-secondary-200">
              <button
                onClick={handleCloseModal}
                className="btn btn-primary w-full"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicSearch;
